import { useEffect, useState } from "react";
import {
  CheckCircle2,
  XCircle,
  CircleAlert,
  Clock3,
  RefreshCcw,
  Loader2,
  ScrollText,
} from "lucide-react";
import {
  approveApproval,
  cancelApproval,
  friendlyAuthError,
  listApprovals,
  rejectApproval,
  type ApprovalRow,
} from "@/lib/api";

export function ApprovalsSection({
  orgId,
  currentUserId,
  canApprove,
}: {
  orgId: string;
  currentUserId: string;
  canApprove: boolean;
}) {
  const [items, setItems] = useState<ApprovalRow[]>([]);
  const [filter, setFilter] = useState<"pending" | "all">("pending");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    try {
      const r = await listApprovals(orgId, filter);
      setItems(r);
      setErr(null);
    } catch (e: unknown) {
      setErr(friendlyAuthError(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, [orgId, filter]);

  async function approve(id: string) {
    setBusy(id);
    try {
      await approveApproval(orgId, id);
      await load();
    } catch (e: unknown) {
      setErr(friendlyAuthError(e));
    } finally {
      setBusy(null);
    }
  }
  async function reject(id: string) {
    const reason = prompt("Reason for rejection? (optional)") ?? undefined;
    setBusy(id);
    try {
      await rejectApproval(orgId, id, reason);
      await load();
    } catch (e: unknown) {
      setErr(friendlyAuthError(e));
    } finally {
      setBusy(null);
    }
  }
  async function cancel(id: string) {
    if (!confirm("Cancel this approval request?")) return;
    setBusy(id);
    try {
      await cancelApproval(orgId, id);
      await load();
    } catch (e: unknown) {
      setErr(friendlyAuthError(e));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div
      className="rounded-2xl bg-[var(--color-surface)] p-5"
      style={{ boxShadow: "0 2px 12px rgba(0,0,0,0.05)" }}
    >
      <div className="mb-3 flex items-center justify-between gap-3">
        <div>
          <h3 className="flex items-center gap-2 text-sm font-semibold text-[var(--color-text)]">
            <ScrollText className="h-4 w-4 text-[var(--color-accent)]" />
            Secret approvals
          </h3>
          <p className="mt-0.5 text-xs text-[var(--color-text-muted)]">
            Updates and deletes on approval-gated vaults wait here for a second pair of eyes.
          </p>
        </div>
        <div className="flex items-center gap-1.5">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value as "pending" | "all")}
            className="field-input w-auto py-1 text-xs"
          >
            <option value="pending">Pending</option>
            <option value="all">All statuses</option>
          </select>
          <button
            type="button"
            onClick={load}
            className="rounded-lg border border-[var(--color-border)] p-1.5 text-[var(--color-text-muted)] hover:bg-[var(--color-surface-3)]"
            aria-label="Refresh"
          >
            <RefreshCcw className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
      {err && (
        <p className="mb-3 rounded-lg border border-[rgba(255,59,48,0.25)] bg-[var(--color-danger-bg)] px-3 py-2 text-xs text-[var(--color-danger)]">
          {err}
        </p>
      )}
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-5 w-5 spin text-[var(--color-text-muted)]" />
        </div>
      ) : items.length === 0 ? (
        <p className="rounded-lg bg-[var(--color-surface-2)] px-3 py-6 text-center text-xs text-[var(--color-text-muted)]">
          No {filter === "pending" ? "pending" : ""} approvals.
        </p>
      ) : (
        <ul className="space-y-2">
          {items.map((a) => {
            const isMyRequest = a.requested_by === currentUserId;
            return (
              <li
                key={a.id}
                className="rounded-xl bg-[var(--color-surface-2)] p-3 text-xs"
              >
                <div className="flex flex-wrap items-start gap-3">
                  <StatusBadge status={a.status} />
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-[var(--color-text)]">
                      <span className="rounded bg-[var(--color-surface-3)] px-1.5 py-0.5 font-mono text-[10px] uppercase text-[var(--color-text-muted)]">
                        {a.kind}
                      </span>{" "}
                      {a.secret_name ?? "(deleted secret)"} in {a.vault_name ?? "vault"}
                    </p>
                    <p className="mt-0.5 text-[var(--color-text-muted)]">
                      Requested by {a.requester_name ?? a.requester_email ?? "—"}{" "}
                      · {new Date(a.created_at).toLocaleString()}
                    </p>
                    {a.reason && (
                      <p className="mt-1 italic text-[var(--color-text-muted)]">"{a.reason}"</p>
                    )}
                    {a.resolved_at && (
                      <p className="mt-1 text-[var(--color-text-subtle)]">
                        {a.status} by {a.resolver_name ?? a.resolver_email ?? "—"} on{" "}
                        {new Date(a.resolved_at).toLocaleString()}
                      </p>
                    )}
                  </div>
                  {a.status === "pending" && (
                    <div className="flex gap-1.5">
                      {canApprove && !isMyRequest && (
                        <>
                          <button
                            type="button"
                            disabled={busy === a.id}
                            onClick={() => approve(a.id)}
                            className="flex items-center gap-1 rounded-lg border border-[var(--color-success)] bg-[var(--color-success-bg)] px-2 py-1 text-[11px] text-[var(--color-success)] hover:opacity-90 disabled:opacity-50"
                          >
                            {busy === a.id ? (
                              <Loader2 className="h-3 w-3 spin" />
                            ) : (
                              <CheckCircle2 className="h-3 w-3" />
                            )}
                            Approve
                          </button>
                          <button
                            type="button"
                            disabled={busy === a.id}
                            onClick={() => reject(a.id)}
                            className="flex items-center gap-1 rounded-lg border border-[rgba(255,59,48,0.3)] bg-[var(--color-danger-bg)] px-2 py-1 text-[11px] text-[var(--color-danger)] hover:opacity-90 disabled:opacity-50"
                          >
                            <XCircle className="h-3 w-3" />
                            Reject
                          </button>
                        </>
                      )}
                      {isMyRequest && (
                        <button
                          type="button"
                          disabled={busy === a.id}
                          onClick={() => cancel(a.id)}
                          className="flex items-center gap-1 rounded-lg border border-[var(--color-border)] px-2 py-1 text-[11px] hover:bg-[var(--color-surface-3)] disabled:opacity-50"
                        >
                          Cancel my request
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: ApprovalRow["status"] }) {
  if (status === "pending")
    return (
      <span className="flex items-center gap-1 rounded-full bg-[var(--color-warning-bg,_#fff5e0)] px-2 py-0.5 text-[10px] font-semibold text-[var(--color-warning,_#a86200)]">
        <Clock3 className="h-3 w-3" /> Pending
      </span>
    );
  if (status === "approved")
    return (
      <span className="flex items-center gap-1 rounded-full bg-[var(--color-success-bg)] px-2 py-0.5 text-[10px] font-semibold text-[var(--color-success)]">
        <CheckCircle2 className="h-3 w-3" /> Approved
      </span>
    );
  if (status === "rejected")
    return (
      <span className="flex items-center gap-1 rounded-full bg-[var(--color-danger-bg)] px-2 py-0.5 text-[10px] font-semibold text-[var(--color-danger)]">
        <XCircle className="h-3 w-3" /> Rejected
      </span>
    );
  if (status === "expired")
    return (
      <span className="flex items-center gap-1 rounded-full bg-[var(--color-surface-3)] px-2 py-0.5 text-[10px] font-semibold text-[var(--color-text-muted)]">
        <CircleAlert className="h-3 w-3" /> Expired
      </span>
    );
  return (
    <span className="flex items-center gap-1 rounded-full bg-[var(--color-surface-3)] px-2 py-0.5 text-[10px] font-semibold text-[var(--color-text-muted)]">
      Cancelled
    </span>
  );
}
